import React, { useState, useRef, useEffect } from 'react';
import { useT } from '../lib/i18n';
import { Database, CheckCircle2, AlertCircle, UploadCloud, Plug, RefreshCw } from 'lucide-react';
import PageShell from '../components/PageShell';
import { SectionCard, KpiTile, Loader, ErrorBox, PageHeader, QueryState } from '../components/charts';
import { useApi } from '../hooks/useApi';
import api from '../lib/api';
import { formatNumber, formatDate, formatDateTime, weekdayShort } from '../lib/format';
import { gapLines } from '../lib/syncResult';

// The groups endpoint carries English taxonomy labels/descriptions; show them
// in plain Dutch (FR via t()). Unknown keys fall back to the backend text.
const GROUP_NL = {
  cafe: ['Café (alle consumpties)', 'Eten, dranken, ijs, extra’s en bouillons die in het café verkocht worden'],
  retail: ['Winkel (niet-café)', 'Lampen (LampTwist) en koffiebonen om mee te nemen'],
  drinks: ['Dranken', 'Alles in de categorie Dranken (+ extra’s bij dranken)'],
  food: ['Voeding', 'De categorie Voeding — alles wat eetbaar is'],
  cookies: ['Koekjes', 'Cookie, Cookie Matcha, Cookies Box, Sablés'],
  breakfast: ['Ontbijt', 'De volledige categorie Ontbijt'],
  vegetable_plate: ['Vegetable Plate', 'Het product “Vegetable Plate” (groenteschotel)'],
  cappuccino: ['Cappuccino', 'Cappuccino'],
  espresso: ['Espresso', 'Koffies op basis van espresso (ook dubbel, macchiato en iced)'],
  matcha: ['Matcha + Genmaicha', 'Matcha- en genmaicha-dranken (zonder Cookie Matcha)'],
  pastries: ['Gebak', 'Zoetigheden, zonder de koekjes'],
  ice_cream: ['IJs', 'De categorie IJs — bolletjes en coupes'],
  coffee: ['Koffie (totaal)', 'De subcategorie Koffie binnen Dranken'],
  tea: ['Thee', 'De subcategorie Thee binnen Dranken'],
  other_drinks: ['Overige dranken', 'Koude dranken, thee en specials — dranken buiten koffie en matcha'],
  savoury: ['Hartige lunch', 'Hartig eten buiten ontbijt, zoet en de Vegetable Plate'],
};

function UploadCard({ onDone }) {
  const t = useT();
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const handle = async (file) => {
    if (!file) return;
    setBusy(true); setError(''); setResult(null);
    try {
      const r = await api.uploadLightspeed(file);
      setResult(r);
      onDone?.();
    } catch (err) {
      setError(err?.response?.data?.detail || t('Upload mislukt.'));
    } finally { setBusy(false); }
  };

  return (
    <SectionCard title={t("LightSpeed-export uploaden")} subtitle={t("Sleep een 'transacties' CSV/XLSX hierheen — data wordt direct toegevoegd")}>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files?.[0]); }}
        className="flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-200 bg-slate-50 py-8 cursor-pointer hover:border-[#cf9a40] transition-colors">
        <UploadCloud className="h-8 w-8 text-[#cf9a40]" />
        <div className="text-sm text-slate-600">{busy ? t('Bezig met importeren…') : t('Klik of sleep een bestand (.csv / .xlsx)')}</div>
        <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" hidden
          onChange={(e) => handle(e.target.files?.[0])} />
      </div>
      {error && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {result && (
        <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <b>{result.filename}</b> {t("geïmporteerd")} — {formatNumber(result.orders)} {t("bonnen")}, {formatNumber(result.lines)} {t("regels")}, {formatNumber(result.products)} {t("producten")}.
        </div>
      )}
    </SectionCard>
  );
}

// --- sync period picker -------------------------------------------------------
// The API sync takes either a trailing window ({days}) or an explicit one
// ({start, end}). Quick chips cover the usual cases; "Aangepast" opens from/to
// inputs. Spans are capped so one request stays well inside the server timeout
// (~1.5 API pages per opening day); longer backfills go through the CLI.
const SYNC_PRESETS = [
  { key: 'today', label: 'Vandaag' },
  { key: 'yesterday', label: 'Gisteren' },
  { key: 'd3', label: '3 dagen', days: 3 },
  { key: 'd7', label: '7 dagen', days: 7 },
  { key: 'd14', label: '14 dagen', days: 14 },
  { key: 'd30', label: '30 dagen', days: 30 },
];
const MAX_SYNC_DAYS = 62;
// Local calendar date as YYYY-MM-DD (toISOString would shift to UTC after 22:00 in Belgium).
const localISO = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return localISO(d); };
const spanDays = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 86400000) + 1;

// Request body for the sync endpoint, or null when the period is invalid.
function syncBody(period) {
  if (period.key === 'today') return { start: daysAgo(0), end: daysAgo(0) };
  if (period.key === 'yesterday') return { start: daysAgo(1), end: daysAgo(1) };
  if (period.key === 'custom') {
    const { start, end } = period;
    if (!start || !end || start > end || spanDays(start, end) > MAX_SYNC_DAYS) return null;
    return { start, end };
  }
  const p = SYNC_PRESETS.find((x) => x.key === period.key);
  return p?.days ? { days: p.days } : null;
}

function SyncPeriod({ value, onChange, dataStart }) {
  const t = useT();
  const isCustom = value.key === 'custom';
  const today = daysAgo(0);
  const chip = (active) => `px-3 py-2 sm:px-2.5 sm:py-1 rounded transition-colors ${active ? 'bg-white text-[#114817] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`;
  const tooLong = isCustom && value.start && value.end && value.start <= value.end && spanDays(value.start, value.end) > MAX_SYNC_DAYS;
  const reversed = isCustom && value.start && value.end && value.start > value.end;
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-slate-500">{t('Periode')}</div>
      <div className="flex flex-wrap items-center gap-1.5">
        <div className="flex flex-wrap items-center bg-slate-100 rounded-md p-0.5 text-xs sm:text-[11px] font-medium">
          {SYNC_PRESETS.map((p) => (
            <button key={p.key} type="button" onClick={() => onChange({ key: p.key })} className={chip(value.key === p.key)}>{t(p.label)}</button>
          ))}
          <button type="button" className={`${chip(isCustom)} ${isCustom ? '!text-[#cf9a40]' : ''}`}
            onClick={() => onChange({ key: 'custom', start: value.start || daysAgo(7), end: value.end || today })}>
            {t('Aangepast')}
          </button>
        </div>
        {isCustom && (
          <div className="flex flex-wrap items-center gap-1 bg-slate-100 rounded-md px-2 py-1 text-xs sm:text-[11px] font-medium text-slate-600">
            <span className="text-slate-400">{t('van')}</span>
            <input type="date" value={value.start || ''} min={dataStart} max={today}
              onChange={(e) => onChange({ ...value, start: e.target.value })}
              className="bg-white rounded border border-slate-200 px-1.5 py-1.5 sm:py-0.5 text-[16px] sm:text-[11px] text-[#114817] focus:outline-none focus:border-[#cf9a40]" />
            <span className="text-slate-400">{t('tot')}</span>
            <input type="date" value={value.end || ''} min={dataStart} max={today}
              onChange={(e) => onChange({ ...value, end: e.target.value })}
              className="bg-white rounded border border-slate-200 px-1.5 py-1.5 sm:py-0.5 text-[16px] sm:text-[11px] text-[#114817] focus:outline-none focus:border-[#cf9a40]" />
          </div>
        )}
      </div>
      {reversed && <p className="text-xs text-red-600">{t('Startdatum ligt na de einddatum.')}</p>}
      {tooLong && <p className="text-xs text-red-600">{t('Maximaal 62 dagen per keer (langere periodes via de server-opdracht sync_lightspeed).')}</p>}
      <p className="text-xs text-slate-400">{t('De standaardsync haalt minstens de laatste 3 dagen op, en verder terug tot de vorige geslaagde sync. Ontbrekende dagen worden automatisch mee opgehaald.')}</p>
    </div>
  );
}

// Human label of a sync period for the button / result line.
function periodLabel(period, t) {
  if (period.key === 'today') return t('vandaag');
  if (period.key === 'yesterday') return t('gisteren');
  if (period.key === 'custom') return period.start && period.end ? `${formatDate(period.start)} – ${formatDate(period.end)}` : t('Aangepast');
  const p = SYNC_PRESETS.find((x) => x.key === period.key);
  return p ? `${t('laatste')} ${p.days} ${t('dagen')}` : '';
}

function LightspeedCard({ onDone }) {
  const t = useT();
  const { data, loading, error, reload } = useApi(() => api.lightspeedStatus(), []);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [period, setPeriod] = useState({ key: 'd3' });
  const autoRan = useRef(false);
  const body = syncBody(period);

  const syncMsg = (r) => {
    const w = r.window ? ` (${formatDate(r.window.start.slice(0, 10))} – ${formatDate(r.window.end.slice(0, 10))})` : '';
    return [`${t('Gesynchroniseerd')}${w}: ${formatNumber(r.sales)} ${t('bonnen')}, ${formatNumber(r.lines)} ${t('regels')}`,
      ...gapLines(r.gaps, t)].join(' · ');
  };

  const run = async (fn, okMsg) => {
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await fn();
      if (okMsg) setMsg(okMsg(r));
      await reload();
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || t('Actie mislukt.'));
    } finally { setBusy(false); }
  };

  // OAuth callback: LightSpeed sends the browser back to this page
  // (LIGHTSPEED_REDIRECT_URI) with ?code=…&state=…. Finish the connection once,
  // automatically, and scrub the one-time code from the address bar.
  useEffect(() => {
    if (!data || autoRan.current) return;
    if (!new URLSearchParams(window.location.search).get('code')) return;
    autoRan.current = true;
    const landing = window.location.href;
    window.history.replaceState(null, '', window.location.pathname);
    if (data.configured) run(() => api.lightspeedConnect({ code: landing }), () => t('Verbonden!'));
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SectionCard title={t("LightSpeed API (automatisch)")}
      subtitle={t("Eenmalig verbinden; daarna haalt de app de verkopen zelf op — bij het openen van de app als de data ouder is dan 12 uur, of met de knop hieronder.")}>
      {loading && <Loader />}
      {error && <ErrorBox error={error} onRetry={reload} />}
      {data && !data.configured && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {t("De API-sleutel staat nog niet op de server (LIGHTSPEED_CLIENT_SECRET). Zet ze in de Coolify-omgeving en herstart.")}
        </div>
      )}
      {data && data.configured && !data.connected && (
        <div className="space-y-3">
          <ol className="list-decimal pl-5 text-sm text-slate-600 space-y-1">
            <li>{t("Klik 'Verbind met LightSpeed' en meld aan met de LightSpeed-login van het café.")}</li>
            <li>{t("Je komt automatisch terug op deze pagina en de verbinding wordt afgerond.")}</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-2">
            <a href={data.authorize_url || '#'}
              className={`inline-flex items-center justify-center gap-2 rounded-lg bg-[#114817] text-white px-4 py-2.5 text-sm font-semibold ${busy ? 'pointer-events-none opacity-60' : ''}`}>
              <Plug className="h-4 w-4" /> {busy ? t("Verbinden…") : t("Verbind met LightSpeed")}
            </a>
          </div>
          {data.redirect_uri && (
            <p className="text-xs text-slate-400 break-all">
              {t("Terugkeer-URL (moet zo geregistreerd staan op de LightSpeed API-client)")}: <code>{data.redirect_uri}</code>
            </p>
          )}
          <button type="button" onClick={() => setShowPaste((v) => !v)}
            className="text-xs text-slate-500 underline underline-offset-2 text-left">
            {t("Kom je op een andere pagina terecht? Plak hier de volledige URL uit de adresbalk.")}
          </button>
          {showPaste && (
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={code} onChange={(e) => setCode(e.target.value)} placeholder={`${data.redirect_uri || 'https://…'}?code=…`}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2.5 text-[16px] sm:text-sm outline-none focus:border-[#cf9a40]" />
              <button disabled={busy || !code.trim()}
                onClick={() => run(() => api.lightspeedConnect({ code }), () => t("Verbonden!"))}
                className="rounded-lg bg-[#cf9a40] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-40">
                {t("Bevestigen")}
              </button>
            </div>
          )}
        </div>
      )}
      {data && data.connected && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-sm text-slate-700">
            <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            <b>{t("Verbonden")}</b> — {t("sinds")} {formatDateTime(data.connected_at)}{data.connected_by ? ` (${data.connected_by})` : ''}
          </div>
          {data.last_sync && (
            <p className="text-xs text-slate-500">
              {t("Laatste API-sync")}: {formatDateTime(data.last_sync.at)} · {data.last_sync.ok ? `${t('ok')} — ${formatNumber(data.last_sync.orders)} ${t('bonnen')}, ${formatNumber(data.last_sync.lines)} ${t('regels')}` : `${t('fout')}: ${data.last_sync.message}`}
              {data.last_sync.window_start && ` · ${t('periode')} ${formatDate(data.last_sync.window_start.slice(0, 10))} – ${formatDate(data.last_sync.window_end.slice(0, 10))}`}
            </p>
          )}
          {data.gaps?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <b>{t('Ontbrekende dagen (verwacht open, maar geen bonnen)')}:</b>{' '}
              {data.gaps.map((g) => `${weekdayShort(g.weekday_nl)} ${formatDate(g.date)}`).join(', ')}.{' '}
              {t('Deze dagen worden bij de volgende sync opnieuw opgehaald.')}
            </div>
          )}
          <SyncPeriod value={period} onChange={setPeriod} dataStart={data.data_start} />
          <div className="flex flex-col sm:flex-row gap-2">
            <button disabled={busy || !body}
              onClick={() => run(() => api.lightspeedSync(body), syncMsg)}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-[#114817] text-white px-4 py-2.5 text-sm font-semibold disabled:opacity-40">
              <RefreshCw className={`h-4 w-4 ${busy ? 'animate-spin' : ''}`} /> {t("Nu synchroniseren")} · {periodLabel(period, t)}
            </button>
            <button disabled={busy} onClick={() => { if (window.confirm(t('LightSpeed ontkoppelen?'))) run(() => api.lightspeedDisconnect()); }}
              className="text-xs text-slate-400 hover:text-red-500 px-2 py-2">{t("Ontkoppelen")}</button>
          </div>
        </div>
      )}
      {data?.last_error && <p className="mt-2 text-xs text-red-600">{data.last_error}</p>}
      {err && <div className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {msg && <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">{msg}</div>}
    </SectionCard>
  );
}

export default function DataStatus() {
  const t = useT();
  const st = useApi(() => api.dataStatus(), []);
  const g = useApi(() => api.groups(), []);
  const d = st.data;
  const ls = d?.lightspeed_sync;

  return (
    <PageShell page="Data" accent="mint">
      <PageHeader title={t("Databron & status")}
        subtitle={t("Welke data zit in de rapportering, hoe vers is ze, en hier voeg je nieuwe LightSpeed-data toe.")} />

      <div className="mb-4"><LightspeedCard onDone={() => st.reload()} /></div>
      <div className="mb-4"><UploadCard onDone={() => st.reload()} /></div>

      {st.error && <ErrorBox error={st.error} onRetry={st.reload} />}
      {st.loading && !d && <Loader />}

      {d && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <KpiTile label={t("Producten")} value={formatNumber(d.tables?.products)} accent="gold" icon={Database} />
            <KpiTile label={t("Kassabonnen")} value={formatNumber(d.tables?.orders)} accent="green" icon={Database} />
            <KpiTile label={t("Bonregels")} value={formatNumber(d.tables?.order_lines)} accent="mint" icon={Database} />
            <KpiTile label={t("Betalingen")} value={formatNumber(d.tables?.payments)} accent="gold" icon={Database} />
          </div>

          <SectionCard title={t("Dataperiode & bronnen")}>
            <div className="flex items-center gap-2 text-sm text-slate-700 mb-3">
              <span className="font-semibold">{formatDate(d.data_range?.start)}</span>
              <span className="text-slate-300">→</span>
              <span className="font-semibold">{formatDate(d.data_range?.end)}</span>
            </div>
            <div className="space-y-2 text-sm text-slate-700">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />
                <span><b>Odoo</b> — {t("kassa tot maart 2026")}</span>
              </div>
              <div className="flex items-start gap-2">
                {ls && ls.ok === false
                  ? <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-[#cf9a40]" />
                  : <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0 text-emerald-500" />}
                <span><b>LightSpeed</b> — {t("kassa sinds maart 2026 — via API of upload hierboven")}</span>
              </div>
            </div>
            {(ls || d.last_sync) && (
              <div className="text-[11px] text-slate-400 mt-3 space-y-0.5">
                {ls && (
                  <p>
                    {t("Laatste LightSpeed-import")}: {formatDateTime(ls.at)}
                    {ls.ok ? ` · ${formatNumber(ls.orders)} ${t('bonnen')}` : ` · ${t('fout')}${ls.message ? `: ${ls.message}` : ''}`}
                  </p>
                )}
                {d.last_sync && (
                  <p>{t("Laatste Odoo-sync")}: {formatDateTime(d.last_sync.at)} · {d.last_sync.ok ? t('ok') : t('fout')}</p>
                )}
              </div>
            )}
          </SectionCard>

          <SectionCard className="mt-4" title={t("Productgroepen (definities)")} subtitle={t("Welke producten elke rapportgroep voedt — zo blijft het transparant")}>
            <QueryState q={g} isEmpty={(x) => !x.groups?.length}>
              {(x) => (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {x.groups.map((grp) => {
                    const [label, desc] = GROUP_NL[grp.key] || [grp.label, grp.description];
                    return (
                      <div key={grp.key} className="rounded-lg border border-slate-100 p-3">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold text-slate-800">{t(label)}</span>
                          <span className="text-xs text-slate-400 shrink-0">{grp.product_count} {t("producten")}</span>
                        </div>
                        {desc && <p className="text-[11px] text-slate-500 mt-0.5">{t(desc)}</p>}
                      </div>
                    );
                  })}
                </div>
              )}
            </QueryState>
          </SectionCard>
        </>
      )}
    </PageShell>
  );
}
